/* LegalLink.jsx — Link cliccabile verso /legal/:slug con info bubble GDPR.
 *
 * Comportamento:
 * - Primo click: mostra tooltip "🔗 Si apre in una nuova scheda. Non perderai
 *   i dati compilati", attende 2s, poi apre la nuova scheda automaticamente.
 *   Imposta `localStorage["legal_link_bubble_seen"]=true`.
 * - Click successivi: apre direttamente senza tooltip.
 * - Bottone "Capito, non mostrare più" dentro la bubble per dismiss esplicito.
 *
 * Accessibilità: role="tooltip", aria-live, dismissibile con ESC.
 */
import { useState, useRef, useEffect } from "react";

const LS_KEY = "legal_link_bubble_seen";
const AUTO_OPEN_MS = 2000;

export default function LegalLink({ slug, children, className = "" }) {
  const [showBubble, setShowBubble] = useState(false);
  const linkRef = useRef(null);
  const timerRef = useRef(null);

  const cleanup = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => cleanup, []);

  const dismissAndOpen = () => {
    try { localStorage.setItem(LS_KEY, "true"); } catch { /* */ }
    setShowBubble(false);
    cleanup();
    try {
      window.open(`/legal/${slug}`, "_blank", "noopener,noreferrer");
    } catch { /* */ }
  };

  const dismissForever = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(LS_KEY, "true"); } catch { /* */ }
    setShowBubble(false);
    cleanup();
  };

  const handleClick = (e) => {
    e.stopPropagation();
    let seen = false;
    try { seen = localStorage.getItem(LS_KEY) === "true"; } catch { /* */ }
    if (seen) return;  // Comportamento standard (<a> default)
    e.preventDefault();
    setShowBubble(true);
    cleanup();
    timerRef.current = setTimeout(() => {
      dismissAndOpen();
    }, AUTO_OPEN_MS);
  };

  // Chiudi con ESC
  useEffect(() => {
    if (!showBubble) return;
    const onKey = (ev) => {
      if (ev.key === "Escape") {
        cleanup();
        setShowBubble(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showBubble]);

  return (
    <span className="relative inline-block">
      <a
        ref={linkRef}
        href={`/legal/${slug}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Apri informativa ${slug} in nuova scheda`}
        data-testid={`legal-link-${slug}`}
        onClick={handleClick}
        className={
          className ||
          "text-emerald-700 underline underline-offset-2 hover:text-emerald-900 font-semibold"
        }
      >
        {children}
        <span aria-hidden="true" className="text-xs ml-0.5">↗</span>
      </a>

      {showBubble && (
        <span
          role="tooltip"
          aria-live="polite"
          data-testid={`legal-link-bubble-${slug}`}
          className="absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full z-50 w-[min(280px,90vw)] rounded-xl bg-white border-2 border-emerald-500 shadow-xl p-3 text-left animate-[fadeIn_0.2s_ease-out]"
          style={{ animation: "fadeIn 0.2s ease-out" }}
        >
          <span className="block text-xs text-slate-700 leading-snug">
            <span aria-hidden="true">🔗</span> Si apre in una nuova scheda.{" "}
            <b>Non perderai i dati compilati</b> del form.
          </span>
          <span className="block text-[10px] text-emerald-700 mt-1.5 font-bold">
            Apertura automatica in 2s…
          </span>
          <button
            type="button"
            onClick={dismissForever}
            data-testid={`legal-link-bubble-dismiss-${slug}`}
            className="mt-2 text-[11px] underline text-slate-500 hover:text-slate-800"
          >
            Capito, non mostrare più
          </button>
          <span
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-white border-r-2 border-b-2 border-emerald-500 rotate-45"
          />
        </span>
      )}
    </span>
  );
}
